import { DeleteOutlined } from '@ant-design/icons';
import { Button, Table } from 'antd';
import { type FormItemProps } from 'antd/es/form';
import { FieldArray, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { type DealerSocialMedia } from '../../api/types';
import ResponsiveStyledTable from '../../pages/shared/ResponsiveStyledTable';
import FormFields from '../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../utilities/extensions';
import FormItem from './FormItem';
import SingleUploadField from './SingleUploadField';

type DealerSocialMediaProps = DealerSocialMedia;

export interface MultipleInputArrayProps {
    name: string;
    label?: string;
    labelForAdd?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    limit?: number;
    disabled?: boolean;
}

const MultipleInputFieldArray = ({
    name,
    label,
    labelForAdd,
    itemProps,
    limit,
    disabled,
    ...props
}: MultipleInputArrayProps) => {
    const { t } = useTranslation('dealerForm');

    const [field] = useField({ name });
    const { value } = field;

    return (
        <FormItem {...itemProps} label={label} name={name}>
            <FieldArray
                {...field}
                name={name}
                render={arrayHelpers => (
                    <ResponsiveStyledTable
                        dataSource={value}
                        pagination={false}
                        rowKey={(row, index: number) => `${name}_${index.toString()}`}
                    >
                        <Table.Column<DealerSocialMediaProps>
                            dataIndex="title"
                            render={(value, record, index) => (
                                <FormFields.TranslatedInputField
                                    disabled={disabled}
                                    name={`${name}.[${index}].title`}
                                />
                            )}
                            title={t('dealerForm:fields.socialMedia.title.label')}
                        />
                        <Table.Column<DealerSocialMediaProps>
                            dataIndex="content"
                            render={(value, record, index) => (
                                <FormFields.InputField disabled={disabled} name={`${name}.[${index}].content`} />
                            )}
                            title={t('dealerForm:fields.socialMedia.content.label')}
                        />
                        <Table.Column<DealerSocialMediaProps>
                            dataIndex="icon"
                            render={(value, record, index) => (
                                <SingleUploadField
                                    disabled={disabled}
                                    extensions={allowedExtensions.image}
                                    name={`${name}.[${index}].icon`}
                                />
                            )}
                            title={t('dealerForm:fields.socialMedia.icon.label')}
                        />
                        <Table.Column
                            key="actions"
                            align="center"
                            render={(_, __, index) =>
                                !disabled ? (
                                    <Button
                                        icon={<DeleteOutlined />}
                                        onClick={() => {
                                            arrayHelpers.remove(index);
                                        }}
                                        size="middle"
                                        type="link"
                                    />
                                ) : null
                            }
                            title={t('dealerForm:actions.action')}
                            width={100}
                        />
                    </ResponsiveStyledTable>
                )}
            />
        </FormItem>
    );
};

export default MultipleInputFieldArray;
